import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormSection } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { StatusIndicator } from '@strata/react-status-indicators';

import { getPrescriberFaxTypeForIntakeCase } from 'services/helpers';
import {
    type FaxResponse,
    FaxStatus,
    type FaxStatusValues,
    FaxType,
    useGetFaxStatusesQuery,
} from 'store/api/intakeCaseApi';

const FaxTypeToLabel = {
    [FaxType.PRESCRIBER_INITIATION]: 'Prior Auth Initiation Request',
    [FaxType.PRIOR_AUTH_NOTIFICATION]: 'Prior Auth Notification',
};

const FaxStatusToStatusIndicatorStatus = {
    [FaxStatus.SUCCESS]: StatusIndicator.Status.SUCCESS,
    [FaxStatus.FAILED]: StatusIndicator.Status.CAUTION,
    [FaxStatus.NOT_SENT]: StatusIndicator.Status.DEFAULT,
    [FaxStatus.NON_PROD]: StatusIndicator.Status.DEFAULT,
};

const FaxStatusToStatusIndicatorMessage = {
    [FaxStatus.SUCCESS]: 'SENT',
    [FaxStatus.FAILED]: 'FAILED',
    [FaxStatus.NOT_SENT]: 'NOT SENT',
    [FaxStatus.NON_PROD]: 'NON-PROD',
};

interface FaxResponseRowProps {
    faxResponse?: FaxResponse;
}

const FaxResponseRow = ({ faxResponse = undefined }: FaxResponseRowProps) => {
    const faxStatus: FaxStatusValues =
        faxResponse?.callbackFaxStatus ?? faxResponse?.faxStatus ?? FaxStatus.NOT_SENT;

    return (
        <div className="sds_fax-status__row">
            <DetailLabelValuePair
                label="Name"
                value={faxResponse?.faxType ? FaxTypeToLabel[faxResponse.faxType] : undefined}
                style={{ width: '33%' }}
            />
            <DetailLabelValuePair
                label="Last Fax Status"
                value={
                    <StatusIndicator
                        message={FaxStatusToStatusIndicatorMessage[faxStatus]}
                        status={
                            FaxStatusToStatusIndicatorStatus[faxStatus] ??
                            StatusIndicator.Status.DEFAULT
                        }
                        tooltipText={faxResponse?.callbackErrorCode ?? faxResponse?.faxResponse}
                    />
                }
                style={{ width: '33%' }}
            />
            <DetailLabelValuePair
                label="Last Fax Sent"
                value={
                    faxStatus !== FaxStatus.NOT_SENT
                        ? formatDate(faxResponse?.dateSent, DateFormats.DATE_TIME)
                        : undefined
                }
                style={{ width: '34%' }}
            />
        </div>
    );
};

interface FaxStatusProps {
    intakeCaseId: string;
    monitoringCase?: boolean;
}

export const FaxStatuses = ({ intakeCaseId, monitoringCase = false }: FaxStatusProps) => {
    const { data, isFetching } = useGetFaxStatusesQuery({ intakeCaseId });
    const faxType = getPrescriberFaxTypeForIntakeCase(monitoringCase);
    const faxResponse = data?.[faxType];

    return (
        <FormSection
            sectionTitle="Fax Status"
            isLoading={isFetching}
            content={
                <div className="sds_fax-status__section">
                    <div className="sds_fax-status__subsection">
                        <div className="sds_fax-status__subtitle">Prescriber Fax</div>
                        <FaxResponseRow faxResponse={faxResponse} />
                    </div>
                </div>
            }
        />
    );
};
